import type { AxiosError } from "axios";
import axios, {
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

import { TOKEN_STORAGE_KEY } from "../config/auth-storage-keys";
import { authStorage } from "../config/session-storage";
// Cookie-session pieces come from the shared package: one refresh loop per
// page, not one per bundle.
import {
  API_BASE_URL as RESOLVED_API_BASE_URL,
  AUTH_COOKIE_SESSION,
  CSRF_HEADER,
  readCsrfCookie,
  recoverFromUnauthorized,
  storedIssuerIdToken,
} from "@penn/web-common";

declare module "axios" {
  // Opt a request into forwarding the end-user's platform issuer id_token as
  // `x-pem-user-authorization` (see the request interceptor). Set only on the
  // self-profile read/write calls.
  export interface AxiosRequestConfig {
    forwardUserToken?: boolean;
  }
}

const API_BASE_URL = RESOLVED_API_BASE_URL;
if (!API_BASE_URL) {
  throw new Error(
    "API config missing — set VITE_API_BASE_URL for this deployment.",
  );
}

const Api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15_000,
  // Required for the `penn_rt` / `penn_csrf` cookies to travel to the sibling
  // API host at all. Business routes do not authenticate from the cookie —
  // `penn_rt` is `Path=/v1/auth`-scoped and the browser never attaches it to
  // them — but `/v1/auth/*` calls made through this instance need it.
  withCredentials: true,
});

/**
 * Fired on `window` whenever any admin API call returns 403. The admin
 * console's `ForbiddenBoundary` (mounted in `DashboardLayout`) listens and
 * swaps the page content for the "you don't have the required permission"
 * panel. Outside the admin console nothing listens, so the event is inert.
 */
export const ADMIN_FORBIDDEN_EVENT = "penn-admin:forbidden";

Api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // The Cognito → Penn JWT exchange ships with `x-skip-penn-token: 1` so a
  // stale Penn token doesn't override the body's Cognito token. Strip it
  // here and skip the auth header attach.
  const skipPenn = config.headers.get?.("x-skip-penn-token");
  if (skipPenn) {
    config.headers.delete("x-skip-penn-token");
  } else {
    const token = authStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }
  }

  // Calls flagged `forwardUserToken` additionally carry the end-user's platform
  // issuer id_token in `x-pem-user-authorization`. svc-03 relays it to the
  // platform registration-api, which reads/writes the shared demographic subset
  // in the central directory (the SigV4 Lattice hop owns `Authorization`, so the
  // user token rides in this separate header). Scoped to the self-profile
  // read/write so the token is not attached to every request. No stored issuer
  // token (a Cognito-mode sign-in) leaves the header off: the read path falls
  // back to local columns, the write path refuses the shared edit.
  if (config.forwardUserToken) {
    const idToken = storedIssuerIdToken();
    if (idToken) {
      config.headers.set("x-pem-user-authorization", `Bearer ${idToken}`);
    }
  }
  config.headers.set("x-auth-scope", "internal");

  // Double-submit CSRF echo, on the two cookie-authenticated routes only.
  // Business routes stay on the in-memory Bearer token and are therefore
  // structurally CSRF-immune; sending it everywhere would imply otherwise.
  const path = config.url ?? "";
  if (
    AUTH_COOKIE_SESSION &&
    (path.includes("/v1/auth/refresh") || path.includes("/v1/auth/logout"))
  ) {
    const csrf = readCsrfCookie();
    if (csrf) config.headers.set(CSRF_HEADER, csrf);
  }

  return config;
});

Api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const url = error.config?.url ?? "";
    const isAuthEndpoint =
      url.includes("/v1/auth/session") || url.includes("/v1/auth/refresh");
    const cfg = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined;

    if (status === 403 && typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent(ADMIN_FORBIDDEN_EVENT, { detail: { url } }),
      );
    }

    if (status === 401 && !isAuthEndpoint && cfg && !cfg._retried) {
      cfg._retried = true;
      // The same recovery as the customer site (web-common): the cookie session
      // if on, the issuer or Cognito re-exchange, then a silent issuer sign-in
      // back to this page, and only then Sign in.
      const recovery = await recoverFromUnauthorized();
      if (recovery.kind === "retry") {
        cfg.headers?.set?.("Authorization", `Bearer ${recovery.token}`);
        return Api.request(cfg);
      }
      // The page is leaving; never settle, so no caller renders an error.
      if (recovery.kind === "navigating") return new Promise<never>(() => {});
    }
    return Promise.reject(error);
  },
);

export default Api;
